(function carouselModule(globalScope) {
  'use strict';

  function createCarouselState(options) {
    const count = Math.max(1, Number(options.count) || 1);
    const reducedMotion = Boolean(options.reducedMotion);
    let index = 0;
    let playing = count > 1 && !reducedMotion;

    function normalize(nextIndex) {
      return ((Number(nextIndex) % count) + count) % count;
    }

    return {
      current() {
        return index;
      },
      goTo(nextIndex) {
        index = normalize(nextIndex);
        return index;
      },
      next() {
        return this.goTo(index + 1);
      },
      previous() {
        return this.goTo(index - 1);
      },
      pause() {
        playing = false;
      },
      play() {
        if (count > 1 && !reducedMotion) playing = true;
      },
      isPlaying() {
        return playing;
      },
    };
  }

  function initCarousel(root, dependencies = {}) {
    if (!root) return null;

    const slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));
    const previousButton = root.querySelector('[data-carousel-previous]');
    const nextButton = root.querySelector('[data-carousel-next]');
    const toggleButton = root.querySelector('[data-carousel-toggle]');
    const documentRef = dependencies.document || root.ownerDocument;
    const windowRef = dependencies.window || globalScope;
    const schedule = dependencies.schedule || setTimeout;
    const cancelSchedule = dependencies.cancelSchedule || clearTimeout;
    const interval = Number(root.dataset.interval) || 5500;
    const reducedMotion = Boolean(
      windowRef && windowRef.matchMedia && windowRef.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
    const state = createCarouselState({ count: slides.length, reducedMotion });
    let timer = null;
    let touchStartX = null;

    function clearTimer() {
      if (timer === null) return;
      cancelSchedule(timer);
      timer = null;
    }

    function render() {
      const current = state.current();
      slides.forEach((slide, index) => {
        const active = index === current;
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, index) => {
        if (index === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (toggleButton) {
        toggleButton.setAttribute('aria-label', state.isPlaying() ? 'Pause slideshow' : 'Play slideshow');
        toggleButton.setAttribute('aria-pressed', state.isPlaying() ? 'false' : 'true');
      }
    }

    function queueNext() {
      clearTimer();
      if (!state.isPlaying() || slides.length <= 1 || (documentRef && documentRef.hidden)) return;
      timer = schedule(() => {
        timer = null;
        state.next();
        render();
        queueNext();
      }, interval);
    }

    function manualGo(directionOrIndex) {
      clearTimer();
      state.pause();
      if (directionOrIndex === 'next') state.next();
      else if (directionOrIndex === 'previous') state.previous();
      else state.goTo(directionOrIndex);
      render();
    }

    const onPrevious = () => manualGo('previous');
    const onNext = () => manualGo('next');
    const dotHandlers = dots.map((_dot, index) => () => manualGo(index));
    const onToggle = () => {
      if (state.isPlaying()) {
        state.pause();
        clearTimer();
      } else {
        state.play();
      }
      render();
      queueNext();
    };
    const onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        manualGo('previous');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        manualGo('next');
      }
    };
    const onTouchStart = (event) => {
      touchStartX = event.touches && event.touches[0] ? event.touches[0].clientX : null;
    };
    const onTouchEnd = (event) => {
      if (touchStartX === null || !event.changedTouches || !event.changedTouches[0]) return;
      const distance = event.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(distance) < 40) return;
      manualGo(distance < 0 ? 'next' : 'previous');
    };
    const onVisibilityChange = () => {
      if (documentRef.hidden) clearTimer();
      else queueNext();
    };

    if (previousButton) previousButton.addEventListener('click', onPrevious);
    if (nextButton) nextButton.addEventListener('click', onNext);
    if (toggleButton) toggleButton.addEventListener('click', onToggle);
    dots.forEach((dot, index) => dot.addEventListener('click', dotHandlers[index]));
    root.addEventListener('keydown', onKeydown);
    root.addEventListener('touchstart', onTouchStart);
    root.addEventListener('touchend', onTouchEnd);
    if (documentRef) documentRef.addEventListener('visibilitychange', onVisibilityChange);

    render();
    queueNext();

    return {
      destroy() {
        clearTimer();
        if (previousButton) previousButton.removeEventListener('click', onPrevious);
        if (nextButton) nextButton.removeEventListener('click', onNext);
        if (toggleButton) toggleButton.removeEventListener('click', onToggle);
        dots.forEach((dot, index) => dot.removeEventListener('click', dotHandlers[index]));
        root.removeEventListener('keydown', onKeydown);
        root.removeEventListener('touchstart', onTouchStart);
        root.removeEventListener('touchend', onTouchEnd);
        if (documentRef) documentRef.removeEventListener('visibilitychange', onVisibilityChange);
      },
    };
  }

  const api = { createCarouselState, initCarousel };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (globalScope) globalScope.WushuCarousel = api;

  if (globalScope && globalScope.document) {
    const initialize = () => {
      globalScope.document.querySelectorAll('[data-wushu-carousel]').forEach((root) => {
        if (root.dataset.carouselReady === 'true') return;
        root.dataset.carouselReady = 'true';
        initCarousel(root);
      });
    };

    if (globalScope.document.readyState === 'loading') {
      globalScope.document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
    globalScope.document.addEventListener('shopify:section:load', initialize);
  }
})(typeof window !== 'undefined' ? window : globalThis);
